// Curated list of Google Fonts available for tenant brand themes.
// Each entry is a pairing: a display label, the Google Fonts family name,
// and a CSS font-family stack (quoted family + fallbacks) used at render time.

export interface BrandFontOption {
  /** Stable id persisted in tenants.branding.slideTheme.fontFamily */
  id: string;
  /** Display label shown in the picker */
  label: string;
  /** Google Fonts family name (used to build the stylesheet URL) */
  family: string;
  /** Full CSS font-family stack, including fallbacks */
  cssStack: string;
  /** Category hint shown as a subtitle in the picker */
  category: 'sans' | 'serif';
}

export const BRAND_FONTS: BrandFontOption[] = [
  { id: 'inter',             label: 'Inter',              family: 'Inter',              cssStack: '"Inter", system-ui, sans-serif',            category: 'sans' },
  { id: 'manrope',           label: 'Manrope',            family: 'Manrope',            cssStack: '"Manrope", system-ui, sans-serif',          category: 'sans' },
  { id: 'dm-sans',           label: 'DM Sans',            family: 'DM Sans',            cssStack: '"DM Sans", system-ui, sans-serif',          category: 'sans' },
  { id: 'plus-jakarta-sans', label: 'Plus Jakarta Sans',  family: 'Plus Jakarta Sans',  cssStack: '"Plus Jakarta Sans", system-ui, sans-serif', category: 'sans' },
  { id: 'poppins',           label: 'Poppins',            family: 'Poppins',            cssStack: '"Poppins", system-ui, sans-serif',          category: 'sans' },
  { id: 'nunito',            label: 'Nunito',             family: 'Nunito',             cssStack: '"Nunito", system-ui, sans-serif',           category: 'sans' },
  { id: 'work-sans',         label: 'Work Sans',          family: 'Work Sans',          cssStack: '"Work Sans", system-ui, sans-serif',        category: 'sans' },
  { id: 'space-grotesk',     label: 'Space Grotesk',      family: 'Space Grotesk',      cssStack: '"Space Grotesk", system-ui, sans-serif',    category: 'sans' },
  { id: 'ibm-plex-sans',     label: 'IBM Plex Sans',      family: 'IBM Plex Sans',      cssStack: '"IBM Plex Sans", system-ui, sans-serif',    category: 'sans' },
  { id: 'raleway',           label: 'Raleway',            family: 'Raleway',            cssStack: '"Raleway", system-ui, sans-serif',          category: 'sans' },
  { id: 'gowun-batang',      label: 'Gowun Batang',       family: 'Gowun Batang',       cssStack: '"Gowun Batang", Georgia, serif',            category: 'serif' },
  { id: 'lora',              label: 'Lora',               family: 'Lora',               cssStack: '"Lora", Georgia, serif',                    category: 'serif' },
  { id: 'merriweather',      label: 'Merriweather',       family: 'Merriweather',       cssStack: '"Merriweather", Georgia, serif',            category: 'serif' },
  { id: 'playfair-display',  label: 'Playfair Display',   family: 'Playfair Display',   cssStack: '"Playfair Display", Georgia, serif',        category: 'serif' },
  { id: 'dm-serif-display',  label: 'DM Serif Display',   family: 'DM Serif Display',   cssStack: '"DM Serif Display", Georgia, serif',        category: 'serif' },
  { id: 'fraunces',          label: 'Fraunces',           family: 'Fraunces',           cssStack: '"Fraunces", Georgia, serif',                category: 'serif' },
  // Added for kula-create visual presets (KULA_BUILDER_INTEGRATION.md §G).
  { id: 'syne',              label: 'Syne',               family: 'Syne',               cssStack: '"Syne", system-ui, sans-serif',             category: 'sans' },
  { id: 'cormorant-garamond',label: 'Cormorant Garamond', family: 'Cormorant Garamond', cssStack: '"Cormorant Garamond", Georgia, serif',      category: 'serif' },
];

export const DEFAULT_BRAND_FONT_ID = 'inter';

export const getBrandFont = (id: string | undefined): BrandFontOption =>
  BRAND_FONTS.find(f => f.id === id) ?? BRAND_FONTS[0];

// Tracks families already injected so repeat calls are a no-op
const loadedFamilies = new Set<string>();

/**
 * Load a Google Font stylesheet on demand. Idempotent — repeat calls with the
 * same family are ignored. Server-safe (no-op when `document` is unavailable).
 * Requests weights 400/500/600/700 which cover the slide typography scale.
 */
export function loadBrandFont(fontId: string | undefined): void {
  if (typeof document === 'undefined') return;
  const font = getBrandFont(fontId);
  if (loadedFamilies.has(font.family)) return;

  const familyParam = font.family.replace(/\s+/g, '+');
  const href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@400;500;600;700&display=swap`;

  if (document.querySelector(`link[data-brand-font="${font.id}"]`)) {
    loadedFamilies.add(font.family);
    return;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.brandFont = font.id;
  document.head.appendChild(link);
  loadedFamilies.add(font.family);
}
